// 표준 상품(`0102`) — 분류·브랜드·별칭·상품은 전역, 브랜드 일부(org_id 있음)와 검수 요청은 조직 것.

import type { BaseRow } from "../common/types";

export type CatalogAttrType = "select" | "number" | "pills" | "text";

// 분류마다 받는 속성 한 칸 — key 가 `color`(CATALOG_COLOR_KEY)면 attrs 가 아니라 colors 열에 담는다.
export interface CatalogAttrDef {
  key: string;
  label: string;
  type: CatalogAttrType;
  options?: string[];
  unit?: string;
}

export type CatalogAttrValue = string | number;

export type CatalogAttrs = Record<string, CatalogAttrValue>;

export interface CatalogCategory extends BaseRow {
  code: string;
  path: string[];
  attrs: CatalogAttrDef[];
  sort: number;
}

// org_id null = 표준 브랜드 · 있으면 그 조직만 보는 브랜드(INSERT_ONLY 라 updated_at 이 없다).
export interface CatalogBrand {
  id: string;
  org_id: string | null;
  name: string;
  created_at: string;
}

export interface CatalogBrandAlias extends BaseRow {
  brand_id: string;
  alias: string;
}

export interface CatalogProduct extends BaseRow {
  category_id: string;
  brand_id: string | null;
  model: string | null;
  name: string;
  gtin: string | null;
  attrs: CatalogAttrs;
  colors: string[];
}

export const CATALOG_BRAND_MODES = ["BRAND", "GENERIC", "UNKNOWN"] as const;
export type CatalogBrandMode = (typeof CATALOG_BRAND_MODES)[number];

export const CATALOG_SUBMISSION_STATUSES = ["PENDING", "APPROVED", "MERGED", "REJECTED"] as const;
export type CatalogSubmissionStatus = (typeof CATALOG_SUBMISSION_STATUSES)[number];

// 검수 요청 — 조직은 PENDING 으로 넣기만 하고 상태·product_id·reviewed_at 은 운영자(D2)가 바꾼다.
export interface CatalogSubmission {
  id: string;
  org_id: string;
  price_item_id: string;
  category_id: string;
  brand_id: string | null;
  brand_mode: CatalogBrandMode;
  model: string | null;
  gtin: string | null;
  attrs: CatalogAttrs;
  colors: string[];
  link: string | null;
  status: CatalogSubmissionStatus;
  product_id: string | null;
  reject_reason: string | null;
  created_by: string | null;
  created_at: string;
  reviewed_at: string | null;
}

// 저장소에 넣는 한 줄 — id 는 앱이 만든다 · 상태는 늘 PENDING.
export type CatalogSubmissionInsert = Pick<
  CatalogSubmission,
  "id" | "price_item_id" | "category_id" | "brand_id" | "brand_mode" | "model" | "gtin" | "attrs" | "colors" | "link" | "created_by"
>;

// 단가 줄의 표준 상품 칸 — 넘긴 키만 바꾼다.
export interface CatalogPriceLinkPatch {
  catalog_product_id?: string | null;
  catalog_opt_out?: boolean;
}

// 단가 줄 표준 상품 상태 — 우선순위는 LINKED > PENDING > OPT_OUT > SUGGESTED > UNLINKED.
export const CATALOG_LINK_STATUSES = ["LINKED", "PENDING", "SUGGESTED", "OPT_OUT", "UNLINKED"] as const;
export type CatalogLinkStatus = (typeof CATALOG_LINK_STATUSES)[number];

export const CATALOG_LINK_LABEL: Record<CatalogLinkStatus, string> = {
  LINKED: "표준 연결",
  PENDING: "검수 대기",
  SUGGESTED: "연결 제안",
  OPT_OUT: "품목만",
  UNLINKED: "미연결",
};

// 단가표 「연결」 거르개 — 전체·표준 연결·미연결(제안 포함)·검수 대기.
export const CATALOG_LINK_FILTERS = ["all", "linked", "unlinked", "pending"] as const;
export type CatalogLinkFilter = (typeof CATALOG_LINK_FILTERS)[number];

// 등록 폼이 받는 것 한 벌 — 분류·브랜드(표준 + 우리 회사)·별칭.
export interface CatalogForm {
  categories: CatalogCategory[];
  brands: CatalogBrand[];
  aliases: CatalogBrandAlias[];
}
